import {
    ElasticsearchDeviceBodyConfig,
    ElasticsearchDirection,
} from 'types/ElasticsearchQueries';
import { IssueTicketOption } from 'types/Issue';
import OpacityIcon from '@mui/icons-material/Opacity';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import HomeIcon from '@mui/icons-material/Home';
import GrainIcon from '@mui/icons-material/Grain';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import PoolIcon from '@mui/icons-material/Pool';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import TonalityIcon from '@mui/icons-material/Tonality';
import DomainIcon from '@mui/icons-material/Domain';
import { FormSelectOption } from 'components/form/types';
import { IssueTroubleshooting } from 'types/User/firebaseTypes/ticket';

export const firebaseConfig = {
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    authDomain: 'tankutility.firebaseapp.com',
    databaseURL:
        process.env.REACT_APP_MODE === 'local'
            ? 'https://test.firebaseio.com'
            : 'https://tankutility.firebaseio.com',
    projectId: 'project-2211471009086701792',
    storageBucket: 'project-2211471009086701792.appspot.com',
    messagingSenderId: '681562899306',
    appId: '1:681562899306:web:7ac6204e127fd37bf35b13',
    measurementId: 'G-07MTQC7TEW',
};

export const rolesById = [
    'Account Owner',
    'Power User',
    'User',
    'Limited Access',
];

export const deliveryStatusDisplayNames = [
    'Backlog',
    'Planned',
    'In Progress',
    'Done',
    'Archived',
];

export const convertDeliveryStatusToDisplayName: {
    [displayName: string]: string;
} = {
    backlog: 'Backlog',
    Backlog: 'Backlog',
    planned: 'Planned',
    Planned: 'Planned',
    in_progress: 'In Progress',
    'in progress': 'In Progress',
    'In Progress': 'In Progress',
    done: 'Done',
    Done: 'Done',
    archived: 'Archived',
    Archived: 'Archived',
};

export const convertDeliveryStatusToStatusColor: {
    [displayName: string]: string;
} = {
    Backlog: '#DFE1E6',
    backlog: '#DFE1E6',
    Planned: '#DFE1E6',
    planned: '#DFE1E6',
    'In Progress': '#DEEBFF',
    in_progress: '#DEEBFF',
    'in progress': '#DEEBFF',
    Done: '#C7F3C8',
    done: '#C7F3C8',
    Archived: '#FF573380',
    archived: '#FF573380',
};

export const convertDeliveryStatusToInternalName: {
    [displayName: string]: string;
} = {
    Backlog: 'backlog',
    backlog: 'backlog',
    Planned: 'planned',
    planned: 'planned',
    'In Progress': 'in_progress',
    in_progress: 'in_progress',
    'in progress': 'in_progress',
    Done: 'done',
    done: 'done',
    Archived: 'archived',
    archived: 'archived',
};

export const consumptionTypeLabels: Record<string, string> = {
    bulk_storage: 'Bulk Storage',
    commercial_industrial: 'Commercial/Industrial',
    cooking: 'Cooking',
    fireplace: 'Fireplace',
    generator: 'Generator',
    heating: 'Heating',
    hot_water: 'Hot Water',
    laundry_dryer: 'Laundry/Dryer',
    pool: 'Pool',
    retail_fill_up: 'Retail Fillup',
    backup_heating: 'Backup Heating',
};

export const consumptionTypeIcons: Record<string, JSX.Element> = {
    bulk_storage: <OpacityIcon data-testid="BulkStorageUsageIcon" />,
    commercial_industrial: <DomainIcon data-testid="CommercialUsageIcon" />,
    cooking: <RestaurantIcon data-testid="CookingUsageIcon" />,
    fireplace: <WhatshotIcon data-testid="FireplaceUsageIcon" />,
    generator: <FlashOnIcon data-testid="GeneratorUsageIcon" />,
    heating: <HomeIcon data-testid="HeatingUsageIcon" />,
    hot_water: <GrainIcon data-testid="HotWaterUsageIcon" />,
    laundry_dryer: <LocalLaundryServiceIcon data-testid="LaundryUsageIcon" />,
    pool: <PoolIcon data-testid="PoolUsageIcon" />,
    retail_fill_up: <LocalGasStationIcon data-testid="RetailFillUpUsageIcon" />,
    backup_heating: <TonalityIcon data-testid="BackupHeatingUsageIcon" />,
};

export const periodicUsageTypesForPropane = [
    'pool',
    'generator',
    'fireplace',
    'backup_heating',
    'bulk_storage',
    'retail_fill_up',
    'commercial_industrial',
];

export const periodicUsageTypesForOil = [
    'generator',
    'backup_heating',
    'bulk_storage',
    'retail_fill_up',
    'commercial_industrial',
];

export const deviceDetailViews = [
    'general',
    'config',
    'alerts',
    'users',
    'frequency',
    'tickets',
    'deliveries',
];

export const deviceDetailAdminViews = [
    'readings',
    'test',
    'health',
    'audit',
    'deployment',
];

export const deviceAlertTooltips = {
    classicAlert:
        'Simple tank level alerting. Every time we get a reading from a tank below the set %, we send an email alert. We will not send more than 1 email an hour.',
    warningThreshold:
        'When a tank reaches the set %, even if is not scheduled to transmit, it will do so immediately. Both sending the reading to our servers and an email alert to you.',
    changeOfValue:
        'When the tank consumes the set %, or a delivery occurs, we send an email alert. We will not send more than 1 email an hour.',
    noUsers:
        'You saved some alerts with no users added. Which is fine, just wanted to let you know.',
};

export const reportIssueOptions: { [option: string]: IssueTicketOption } = {
    offline: {
        text: 'Offline',
        follow_up:
            'We have submitted a ticket to our Support team and will send you a confirmation email shortly!',
        confirm: false,
        action: 'none',
        support_doc_url:
            'https://support.tankutility.com/hc/en-us/articles/360033887073-Cell-Monitor-Stopped-Sending-Readings',
        order: 1,
    },
    lostDamaged: {
        text: 'Lost or Damaged',
        follow_up:
            'We have removed this monitor from your Accounts/Live Monitoring view and submitted a ticket to our Support team. We will send you an email confirmation shortly!.',
        confirm: true,
        action: 'retire',
        confirm_text:
            'This will flag the monitor as Inactive and remove it from your Accounts/Live Monitoring lists. You can still view all of your Inactive monitors by editing your filter settings.',
        support_doc_url:
            'https://support.tankutility.com/hc/en-us/articles/360023082014-Tank-Utility-Warranty',
        order: 2,
    },
    inconsistent: {
        text: 'Inconsistent Readings',
        follow_up:
            'We have submitted a ticket to our Support team and will send you a confirmation email shortly!',
        confirm: false,
        action: 'none',
        support_doc_url:
            'https://support.tankutility.com/hc/en-us/articles/360033213294-Inconsistent-Readings',
        order: 3,
    },
    returnedReplaced: {
        text: 'Returned or Replaced',
        follow_up:
            'We have removed this monitor from your Accounts/Live Monitoring view and submitted a ticket to our Support team. We will send you an email confirmation shortly!',
        confirm: true,
        action: 'retire',
        confirm_text:
            'This will flag the monitor as Inactive and remove it from your Accounts/Live Monitoring lists. You can still view all of your Inactive monitors by editing your filter settings.',
        support_doc_url:
            'https://support.tankutility.com/hc/en-us/articles/360023082014-Tank-Utility-Warranty',
        order: 4,
    },
    other: {
        text: 'Other',
        follow_up:
            'We have submitted a ticket to our Support team and will send you a confirmation email shortly!',
        confirm: false,
        action: 'none',
        support_doc_url: '',
        order: 6,
    },
    clearData: {
        text: 'Clear Monitor Data',
        follow_up:
            // eslint-disable-next-line quotes
            "All monitor data has been cleared. Don't hesitate to submit a support ticket with any questions!",
        confirm: true,
        action: 'clear',
        confirm_text:
            'This process will clear all monitor data, including configuration and historical readings. This is irreversible.',
        support_doc_url:
            'https://support.tankutility.com/hc/en-us/articles/360046547033-Can-I-Turn-Off-My-Monitor-',
        order: 5,
    },
};

export const DefaultEsQueryConfig: ElasticsearchDeviceBodyConfig = {
    limit: 50,
    direction: 'DESC' as ElasticsearchDirection,
    offset: 0,
    sortField: 'lastReading.tank',
    search: '',
};

/** Mui sorting event https://mui.com/x/react-data-grid/sorting/ direction mapped to what ES expects. */
export const MuiDirectionToEsDirection: {
    [muiDirection: string]: ElasticsearchDirection;
} = {
    asc: 'ASC',
    desc: 'DESC',
};

export const efficiencyTooltips = {
    costPerDelivery:
        'Your delivery cost estimate is used to calculate your savings. If you are not sure what to use here, a typical industry estimation is $60.',
    savedPerMonth:
        'Your monthly savings is calculated using your number of deliveries avoided and your delivery cost estimate (decided above).',
    savedPerTank:
        'Your tank savings are determined by considering your monthly savings and forecasting your total savings for a complete calendar year, while also factoring in the total number of monitors on your account.',
    moreRevenue:
        'Your revenue increase is determined by comparing your average delivery size with a more typical industry average of 31%.',
    deliveryAverage:
        //eslint-disable-next-line quotes
        "Your average delivery size is the percentage of the tank's capacity filled per delivery.",
    deliveryTarget:
        //eslint-disable-next-line quotes
        'Your expected delivery target percentage.',
};

/** Dropdown select mapping of ticket trigger values to display names. */
export const ticketTriggerOptions: FormSelectOption[] = [
    { value: 'stuck_gauge', name: 'Stuck Gauge' },
    { value: 'missing_address', name: 'Missing Address' },
    { value: 'offline', name: 'Offline' },
    { value: 'battery_crit', name: 'Battery Critical' },
    { value: 'battery_warn', name: 'Battery Warning' },
];

export const ticketTriggerToDisplayName: Record<string, string> = {
    stuck_gauge: 'Stuck Gauge',
    missing_address: 'Missing Address',
    offline: 'Offline',
    battery_crit: 'Battery Critical',
    battery_warn: 'Battery Warning',
};

export const defaultIssueCauseFilters: Record<string, boolean> = {
    stuck_gauge: true,
    missing_address: true,
    offline: true,
    battery_crit: true,
    battery_warn: true,
};

export const ticketTriggerToTroubleshooting: IssueTroubleshooting = {
    stuck_gauge: {
        title: 'Potential Stuck Gauge',
        description:
            'We keep track of how much a gauge moves over the course of a few days. If the movement does not match what we expect for the consumption profile, the gauge may be stuck. There is a chance this alert can be falsely triggered by tanks with irregular usage.',
    },
    missing_address: {
        title: 'Missing Address',
        description:
            'To help keep track of your deployment of Generac Fuel monitors we recommend filling in the full address for all monitors.',
    },
    offline: {
        title: 'Offline',
        description:
            'We have not heard from this monitor in 5 days. If it is in a poor connectivity area, you may want to redeploy it. If an LED sequence was selected during troubleshooting, it will appear below.',
        supportDocsUrl:
            'https://support.tankutility.com/hc/en-us/articles/360033887073-Cell-Monitor-Stopped-Sending-Readings',
    },
    battery_crit: {
        title: 'Battery Critical',
        description:
            'The batteries in this monitor are almost dead. They need to replaced immediately to avoid going offline.',
    },
    battery_warn: {
        title: 'Battery Warning',
        description:
            'The batteries in this monitor are getting low. You should to plan to change the batteries soon.',
    },
};

export const TankUtilityBlue = '#00b8d4';
export const GeneracOrange = '#fa6600';
export const GeneracCharcoal = '#242729';
export const GeneracRed = '#BF3628';
export const GeneracMagicHourBlue = '#00263e';
export const GeneracOffWhite = '#e2e1df';
export const GeneracYellow = '#FDB813';
export const GeneracWhite = '#FFFFFF';

export const defaultLiveMonitoringListSortField = 'duf_7';
export const defaultLiveMonitoringListSortDirection = 'asc';

export const geocodeErrorMessages: { [key: string]: string } = {
    ZERO_RESULTS: 'No results found for this address.',
    INVALID_REQUEST:
        'There was a problem with this address. Please reenter your response and try again.',
    UNKNOWN_ERROR: 'Something went wrong. Please try again.',
};

export const verticalHeightBreakpoint = 845;
